import { Grid, Image, Typography } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import logo from 'assets/images/farmers-plan-logo.svg';
import { FarmHierarchyContext } from 'components/FarmHierarchyContext';
import { FarmPickerField } from 'components/FarmPickerField';
import { SectionNavigation } from 'components/SectionNavigation';
import { useOptionalAppSelectionHeader } from 'context/AppSelectionHeaderContext';
import { useAccountInfo } from 'hooks/useAccount';
import { TFunction } from 'types';
import {
  findFarmOptionById,
  getStoredFarmContext,
  getStringValue,
  getUserFarmOptions,
  setCurrentFarmContext,
} from 'farmContext';

const getPersonName = (accountInfo?: { firstName?: string; email?: string }) => {
  const firstName = getStringValue(accountInfo?.firstName);
  if (firstName) {
    return `${firstName}!`.trim();
  }

  // Accounts created without a first name still get a greeting from the email local-part.
  const email = getStringValue(accountInfo?.email);
  const localPart = email.includes('@') ? email.slice(0, email.indexOf('@')).trim() : '';
  return localPart ? `${localPart}!` : '';
};

export function LayoutIdentityHeader({
  maxWidth = 960,
  sidePadding = 24,
  showMenu = false,
}: {
  maxWidth?: number;
  sidePadding?: number;
  showMenu?: boolean;
}) {
  const screens = Grid.useBreakpoint();
  const { t }: TFunction = useTranslation();
  const { data: accountInfo } = useAccountInfo();
  const headerContext = useOptionalAppSelectionHeader();
  const farmPicker = headerContext?.farmPicker ?? null;
  const isCompact = !screens.md;
  const farmOptions = getUserFarmOptions(accountInfo);
  const storedFarmContext = getStoredFarmContext();
  const storedFarmId = storedFarmContext.farmId || '';
  const selectedFarmOption =
    findFarmOptionById(farmOptions, storedFarmId) ||
    findFarmOptionById(farmOptions, accountInfo?.selectedFarmId);
  const personName = getPersonName(accountInfo);
  const greeting = personName
    ? t('HeaderGreeting', { name: personName })
    : accountInfo
      ? t('HeaderGreetingFallback')
      : '';
  const hasRightContent = Boolean(greeting || showMenu);
  const mobileRightAlignment = greeting && showMenu ? 'space-between' : showMenu ? 'flex-end' : 'flex-start';

  useEffect(() => {
    if (farmPicker || !selectedFarmOption?.farmId) {
      return;
    }

    setCurrentFarmContext({
      farmId: selectedFarmOption.farmId,
      farmName: selectedFarmOption.farmName,
      orgNumber: selectedFarmOption.orgNumber,
    });
  }, [
    farmPicker,
    selectedFarmOption?.farmId,
    selectedFarmOption?.farmName,
    selectedFarmOption?.orgNumber,
  ]);

  return (
    <div
      style={{
        width: '100%',
        padding: `0 ${sidePadding}px`,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          maxWidth,
          margin: '0 auto',
          display: 'flex',
          flexDirection: isCompact ? 'column' : 'row',
          alignItems: isCompact ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: isCompact ? 16 : 24,
        }}
      >
        <div
          style={{
            minWidth: 0,
            flex: 1,
            display: 'flex',
            flexDirection: isCompact ? 'column' : 'row',
            alignItems: isCompact ? 'stretch' : 'center',
            gap: isCompact ? 16 : 24,
          }}
        >
          <Image
            src={logo}
            alt="Farmers Plan Logo"
            preview={false}
            style={{
              width: isCompact ? 152 : 172,
              flexShrink: 0,
            }}
          />
          {farmPicker ? (
            <div
              style={{
                flex: 1,
                minWidth: 0,
                maxWidth: isCompact ? '100%' : 480,
              }}
            >
              <FarmPickerField
                disabled={farmPicker.disabled}
                label={farmPicker.label}
                loading={farmPicker.loading}
                onChange={farmPicker.onChange}
                options={farmPicker.options}
                placeholder={farmPicker.placeholder}
                value={farmPicker.value}
              />
              <FarmHierarchyContext
                hierarchy={farmPicker.hierarchy ?? null}
                farmRoleName={farmPicker.farmRoleName}
                compact
                rolesOnly
              />
            </div>
          ) : null}
        </div>

        {hasRightContent ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isCompact ? mobileRightAlignment : 'flex-end',
              gap: isCompact ? 12 : 16,
              width: isCompact ? '100%' : 'auto',
              flexShrink: 0,
            }}
          >
            {greeting ? (
              <div
                style={{
                  textAlign: isCompact ? 'left' : 'right',
                }}
              >
                <Typography.Text
                  style={{
                    display: 'block',
                    fontSize: isCompact ? 16 : 18,
                    lineHeight: 1.3,
                    fontWeight: 600,
                    color: '#344054',
                  }}
                >
                  {greeting}
                </Typography.Text>
              </div>
            ) : null}
            {showMenu ? <SectionNavigation compact /> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
